import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { diagnosticoService } from '@/services/diagnosticoService'
import {
  CampoDiagnostico,
  DiagnosticoReserva,
  DiagnosticoRespostaInput,
  SugestaoCampoDiagnostico,
} from '@/types/estruturacao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import {
  CampoDiagnosticoInput,
  ValorDiagnostico,
  valorVazio,
} from './CampoDiagnosticoInput'
import { CapturaInteligenteOcr } from './CapturaInteligenteOcr'

interface Props {
  reservaId: string
  /** Nulo em reserva anterior ao vínculo formal com produto — sem campos a exibir. */
  produtoId: string | null
}

/** Chave da sugestão ("receitaLiquidaAnterior") casa com o rótulo ("Receita Líquida Anterior"). */
const normalizar = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase()

/**
 * Número vindo do OCR como texto. Regra segura: com vírgula é formato BR ("1.234,56" →
 * ponto é milhar, vírgula é decimal); só com ponto é decimal US ("1234.56"). Prefixo
 * "R$", espaços e sinal são tolerados. null = não é número.
 */
export const parseNumeroSugerido = (bruto: string | number): number | null => {
  if (typeof bruto === 'number') return Number.isFinite(bruto) ? bruto : null
  const texto = String(bruto).replace(/R\$/g, '').replace(/\s/g, '')
  if (texto === '') return null
  const normalizado = texto.includes(',')
    ? texto.replace(/\./g, '').replace(',', '.')
    : texto
  if (!/^[-+]?\d*\.?\d+$/.test(normalizado)) return null
  const numero = Number(normalizado)
  return Number.isFinite(numero) ? numero : null
}

/** Converte o valor sugerido para o formato do campo; null = não dá para aplicar. */
const converterSugestao = (
  campo: CampoDiagnostico,
  sugestao: SugestaoCampoDiagnostico
): ValorDiagnostico | null => {
  const bruto = sugestao.valor
  switch (campo.tipo) {
    case 'NUMERO':
    case 'MOEDA': {
      const numero = parseNumeroSugerido(bruto)
      if (numero == null) return null
      return { ...valorVazio(), valorNumero: numero }
    }
    case 'DATA': {
      const texto = String(bruto)
      if (/^\d{4}-\d{2}-\d{2}/.test(texto)) return { ...valorVazio(), valorData: texto.slice(0, 10) }
      const br = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (br) return { ...valorVazio(), valorData: `${br[3]}-${br[2]}-${br[1]}` }
      return null
    }
    default:
      return { ...valorVazio(), valorTexto: String(bruto) }
  }
}

/**
 * Etapa 1 — Diagnóstico: formulário gerado a partir dos campos cadastrados no produto
 * (campos são dado, não código) + bloco "Captura inteligente · OCR".
 *
 * Sugestões da IA aparecem como PROPOSTA, com "Aplicar" por campo: aplicar só preenche o
 * formulário; nada é gravado sem o usuário salvar (regra do Figma e do item 41).
 */
export const EtapaDiagnostico: FC<Props> = ({ reservaId, produtoId }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [campos, setCampos] = useState<CampoDiagnostico[]>([])
  const [diagnostico, setDiagnostico] = useState<DiagnosticoReserva | null>(null)
  const [valores, setValores] = useState<Record<string, ValorDiagnostico>>({})
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(false)
    try {
      const [listaCampos, diag] = await Promise.all([
        produtoId ? diagnosticoService.camposDoProduto(produtoId) : Promise.resolve([]),
        diagnosticoService.obter(reservaId),
      ])
      setCampos(listaCampos)
      setDiagnostico(diag)
      const iniciais: Record<string, ValorDiagnostico> = {}
      for (const r of diag.respostas) {
        iniciais[r.campoId] = {
          valorTexto: r.valorTexto,
          valorNumero: r.valorNumero,
          valorData: r.valorData,
        }
      }
      setValores(iniciais)
    } catch {
      setErro(true)
    } finally {
      setCarregando(false)
    }
  }, [reservaId, produtoId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  /** Só rebusca o diagnóstico (sugestões assíncronas), sem descartar o que foi digitado. */
  const atualizarSugestoes = useCallback(async () => {
    try {
      setDiagnostico(await diagnosticoService.obter(reservaId))
    } catch {
      enqueueSnackbar('Não foi possível atualizar as sugestões', { variant: 'error' })
    }
  }, [reservaId, enqueueSnackbar])

  const sugestoes = useMemo(() => {
    const entradas = Object.entries(diagnostico?.sugestoes?.campos ?? {})
    const comCampo: { chave: string; sugestao: SugestaoCampoDiagnostico; campo: CampoDiagnostico }[] = []
    const semCampo: { chave: string; sugestao: SugestaoCampoDiagnostico }[] = []
    for (const [chave, sugestao] of entradas) {
      if (sugestao == null || sugestao.valor == null) continue
      const campo = campos.find((c) => normalizar(c.rotulo) === normalizar(chave))
      if (campo) comCampo.push({ chave, sugestao, campo })
      else semCampo.push({ chave, sugestao })
    }
    return { comCampo, semCampo }
  }, [diagnostico, campos])

  const aplicar = (campo: CampoDiagnostico, sugestao: SugestaoCampoDiagnostico) => {
    const valor = converterSugestao(campo, sugestao)
    if (!valor) {
      enqueueSnackbar(`Não foi possível aplicar a sugestão em "${campo.rotulo}"`, {
        variant: 'warning',
      })
      return
    }
    setValores((prev) => ({ ...prev, [campo.id]: valor }))
    enqueueSnackbar(`Sugestão aplicada em "${campo.rotulo}" — salve para confirmar`, {
      variant: 'info',
    })
  }

  const salvar = async () => {
    setSalvando(true)
    try {
      const payload: DiagnosticoRespostaInput[] = campos.map((campo) => {
        const v = valores[campo.id] ?? valorVazio()
        return {
          campoId: campo.id,
          valorTexto: v.valorTexto,
          valorNumero: v.valorNumero,
          valorData: v.valorData,
        }
      })
      const resultado = await diagnosticoService.salvar(reservaId, payload)
      setDiagnostico(resultado)
      enqueueSnackbar('Diagnóstico salvo', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message || 'Não foi possível salvar o diagnóstico',
        { variant: 'error' }
      )
    } finally {
      setSalvando(false)
    }
  }

  if (carregando) return <EstadoDeLista estado="carregando" />
  if (erro) {
    return (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar o diagnóstico"
        acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
      />
    )
  }

  return (
    <Box>
      {sugestoes.comCampo.length + sugestoes.semCampo.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <AutoAwesomeIcon fontSize="small" color="primary" />
            <Typography sx={{ fontWeight: 700 }}>Sugestões da captura inteligente</Typography>
            {diagnostico?.sugestoes?.atualizadoEm && (
              <Typography variant="caption" color="text.secondary">
                atualizado em {new Date(diagnostico.sugestoes.atualizadoEm).toLocaleString('pt-BR')}
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Propostas extraídas dos documentos — nada é gravado sem você aplicar e salvar.
          </Typography>
          <Stack spacing={1}>
            {sugestoes.comCampo.map(({ chave, sugestao, campo }) => (
              <Stack key={chave} direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 200 }}>
                  {campo.rotulo}
                </Typography>
                <Typography variant="body2">{String(sugestao.valor)}</Typography>
                {typeof sugestao.confianca === 'number' && (
                  <Chip size="small" label={`${Math.round(sugestao.confianca * 100)}%`} />
                )}
                {sugestao.fonte && (
                  <Typography variant="caption" color="text.secondary">
                    {sugestao.fonte}
                  </Typography>
                )}
                <Button size="small" onClick={() => aplicar(campo, sugestao)}>
                  Aplicar
                </Button>
              </Stack>
            ))}
            {sugestoes.semCampo.map(({ chave, sugestao }) => (
              <Stack key={chave} direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
                  {chave}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {String(sugestao.valor)} — sem campo correspondente no diagnóstico
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {campos.length === 0 ? (
        <Alert severity="info">
          {produtoId
            ? 'Este produto ainda não tem campos de diagnóstico cadastrados — o administrador cadastra na aba Diagnóstico da tela do produto.'
            : 'Esta reserva não tem produto do catálogo vinculado — sem produto, não há campos de diagnóstico a responder.'}
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            {campos.map((campo) => (
              <CampoDiagnosticoInput
                key={campo.id}
                campo={campo}
                valor={valores[campo.id] ?? valorVazio()}
                onChange={(v) => setValores((prev) => ({ ...prev, [campo.id]: v }))}
                disabled={salvando}
              />
            ))}
          </Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => void salvar()}
              disabled={salvando}
              startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Salvar diagnóstico
            </Button>
          </Stack>
        </>
      )}

      <Divider sx={{ my: 3 }} />
      <CapturaInteligenteOcr reservaId={reservaId} onAtualizarSugestoes={() => void atualizarSugestoes()} />
    </Box>
  )
}

export default EtapaDiagnostico
